import styles from "./styles.module.scss";
import Modal from "@mui/material/Modal/Modal";
import Button from "@mui/material/Button/Button";
import closeIconModal from "./../../../../assets/_CloseIconModal_.svg";
import { Controller, useForm } from "react-hook-form";
import Autocomplete from "@mui/material/Autocomplete/Autocomplete";
import TextField from "@mui/material/TextField/TextField";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { DatePicker } from "@mui/x-date-pickers/DatePicker/DatePicker";
import { PerfilTimeGrid } from "../PerfilTimeGrid";
import { IKeyText } from "../../../../@types/key-value.type";
import { getAllPositions } from "../../../../services/api/positions/PositionsService";
import { IPosition } from "../../../../@types/position.type";
import { getAllSeniority } from "../../../../services/api/profile/ProfileService";
import { ISeniority } from "../../../../@types/seniority.type";
import { IProjectBudget, IProjectBudgetMonthlies, IProjectBudgetMonthliesPage } from "../../../../@types/project/project-budget.type";
import { addOrUpdateProjectBudget, getProjectBudgetsById } from "../../../../services/api/ProjectBudget/ProjectBudgetService";
import { IApiResponse } from "../../../../@types/api-response.type";
import { formatCurrency } from "../../../../utils/formatCurrency";
import { storageUserGet } from "../../../../storage/storageUser";

interface AddContractedPerfilProps {
  openModal: boolean,
  handleCloseModal: () => void,
  projectId: string,
  id: string,
}

dayjs.extend(utc);
dayjs.extend(timezone);

const isDateValid = (date?: Date | null): boolean => {
  return date !== null && date !== undefined && !isNaN(date.getTime());
};

const perfilSchema = z
  .object({
    positionId: z.string().optional(),
    seniorityLevelId: z.string().optional(),
    positionCost: z.number().optional(),
    salePrice: z.number().optional(),
    startDate: z
      .string()
      .nullable()
      .optional()
      .refine(
        (val) => {
          if (!val) return true;
          const date = new Date(val);
          return isDateValid(date);
        },
        {
          message: "Data Início inválida",
        }
      ),
    endDate: z
      .string()
      .nullable()
      .optional()
      .refine(
        (val) => {
          if (!val) return true;
          const date = new Date(val);
          return isDateValid(date);
        },
        {
          message: "Data Fim inválida",
        }
      ),
  }).refine(
    (data) => {
      const startDate = data.startDate ? new Date(data.startDate) : null;
      const endDate = data.endDate ? new Date(data.endDate) : null;
      if (startDate && endDate) {
        return endDate >= startDate;
      }
      return true;
    },
    {
      message: "Data Fim deve ser maior ou igual à Data Início",
      path: ["endDate"],
    }
  );

  
export type perfilData = z.infer<typeof perfilSchema>;

export const AddContractedPerfil: React.FC<AddContractedPerfilProps> = ({openModal, handleCloseModal, projectId, id}) => { 
  const [positionNameOptions, setPositionNameOptions] = useState<IKeyText[]>([]);
  const [seniorityNameOptions, setSeniorityNameOptions] = useState<IKeyText[]>([]);

  const [gridArray, setGridArray] = useState<IProjectBudgetMonthliesPage[]>();
  const [editObj, setEditObj] = useState<IProjectBudgetMonthlies[]>();

  const {
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors },
  } = useForm<perfilData>({
    resolver: zodResolver(perfilSchema),
    defaultValues: {
      positionId: "",
    },
  });
  
  const loadDomainData = async () => {
    try {
      const responsePosition = await getAllPositions();
      const responseSeniority = await getAllSeniority();
      const PositionOptions = responsePosition.result.map(
        (item: IPosition) => ({
          key: item.id,
          text: item.positionName,
        })
      );
      const seniorityOptions = responseSeniority.map(
        (item: ISeniority) => ({
          key: item.id,
          text: item.seniorityLevelName,
        })
      );
      setPositionNameOptions(PositionOptions);
      setSeniorityNameOptions(seniorityOptions);
    } catch (error) {
      console.log(error);
    }
  };

  const loadEditData = async (id: string) =>{
    const response = (await getProjectBudgetsById(id)) as IApiResponse<IProjectBudget>;
    if(response.statusCode === 200){
      reset({
        positionId: response.result.positionId,
        seniorityLevelId: response.result.seniorityLevelId,
        positionCost: response.result.budget,
        salePrice: response.result.salePrice,
        startDate: response.result.startDate,
        endDate:  response.result.endDate,
      });
      setEditObj(response.result.projectBudgetMonthlies);
      createGrid(response.result.projectBudgetMonthlies);
    }
  }

  useEffect(() => {
    loadDomainData();
  }, []);

  useEffect(() => {
    if(id){
      loadEditData(id);
    }else{
      reset({
        positionId: "",
        seniorityLevelId: "",
        positionCost: 0,
        salePrice: 0,
        startDate: null,
        endDate:  null,
      });
      setGridArray(undefined)
    }
  }, [id]);

  async function handleFormSubmit(data){
    if(gridArray){
      const array = gridArray.map(function (x) { 
        return { 
          month: x.month,
          year: x.year,
          workingHours: x.workingHours,
          projectBudgetId: id ? id : null
        } as IProjectBudgetMonthlies});

      const budget = {
        userId: storageUserGet().id,
        id: id ? id : null,
        projectId: projectId,
        positionId: data.positionId,
        seniorityLevelId: data.seniorityLevelId,
        budget: data.positionCost,
        salePrice: data.salePrice,
        startDate: dayjs(data.startDate).startOf('month').toISOString(),
        endDate: dayjs(data.endDate).endOf('month').toISOString(),
        projectBudgetMonthlies: array,
      } as IProjectBudget;

      const response = await addOrUpdateProjectBudget(budget);
      if(response.statusCode === 200){
        reset({
          positionId: "",
          seniorityLevelId: "",
          positionCost: 0,
          salePrice: 0,
          startDate: null,
          endDate:  null,
        });
        setGridArray(undefined);
        handleCloseModal();
      }
    }
  }

  function createGrid (obj: IProjectBudgetMonthlies[]) {
    setGridArray(undefined);
    if(watch("startDate") && watch("endDate")){
      const startDate = dayjs(watch("startDate")).startOf('month');
      const endDate = dayjs(watch("endDate")).endOf('month');
      if(endDate > startDate){
          const monthDiff = endDate.diff(startDate, 'month') + 1;
          let array = Array<IProjectBudgetMonthliesPage>();
          for (let index = 0; index < monthDiff; index++) {
            const month = startDate.add(index, 'month');
            const element = {
              monthAndYear: month.format('MMMM') + " de " + month.format('YYYY'),
              month: parseInt(month.format('MM')),
              year: parseInt(month.format('YYYY')),
              workingHours: id && obj[index] ? obj[index].workingHours : 168
            } as IProjectBudgetMonthliesPage;
            array.push(element); 
          }
          
          setGridArray(array);
      }
    }
  }
  
  return (
    <Modal open={openModal} onClose={handleCloseModal}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h3>Adicionar Perfil</h3>
          <Button onClick={handleCloseModal} className={styles.iconButton}>
            {closeIconModal && <img src={closeIconModal} />}
          </Button>
        </div>
        <div className={styles.container}>
          <form
            className={styles.formPerfil}
            onSubmit={handleSubmit(handleFormSubmit)}
          >
            <div className={styles.firstLine}>
              <Controller
                name="positionId"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Autocomplete
                    className={styles.position}
                    options={positionNameOptions}
                    getOptionLabel={(option) => option.text}
                    value={
                      positionNameOptions.find(
                        (option) => option.key === field.value
                      ) || null
                    }
                    onChange={(_, newValue) => field.onChange(newValue?.key || "")}
                    noOptionsText="Sem opções"
                    renderOption={(props, option) => {
                      return (
                        <li {...props} key={option.key}>
                          {option.text}
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Perfil"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        placeholder={"Insira o perfil aqui..."}
                        error={!!errors.positionId}
                        helperText={errors.positionId?.message}
                      />
                    )}
                  />
                )}
              />

              <Controller
                name="seniorityLevelId"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Autocomplete
                    className={styles.seniorityLevel}
                    options={seniorityNameOptions}
                    getOptionLabel={(option) => option.text}
                    value={
                      seniorityNameOptions.find(
                        (option) => option.key === field.value
                      ) || null
                    }
                    onChange={(_, newValue) => field.onChange(newValue?.key || "")}
                    noOptionsText="Sem opções"
                    renderOption={(props, option) => {
                      return (
                        <li {...props} key={option.key}>
                          {option.text}
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Nível"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        placeholder={"Insira o nivel aqui..."}
                        error={!!errors.positionId}
                        helperText={errors.positionId?.message}
                      />
                    )}
                    // disabled={!canEdit}
                  />
                )}
              />

              <Controller
                name="positionCost"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    label="Custo/h"
                    className={styles.positionCost}
                    placeholder={"Insira o preço de custo aqui..."}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    value={formatCurrency(field.value)} 
                    onChange={(e) => {
                      const unformattedValue = e.target.value.replace(/\D/g, '');
                      field.onChange(unformattedValue ? Number(unformattedValue) / 100 : 0); 
                    }}
                    error={!!errors.positionCost}
                    helperText={errors.positionCost?.message}
                    // disabled={!canEdit}
                  />
                )}
              />

              <Controller
                name="salePrice"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    label="Venda/h"
                    className={styles.salePrice}
                    placeholder={"Insira o preço de venda aqui..."}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    value={formatCurrency(field.value)} 
                    onChange={(e) => {
                      const unformattedValue = e.target.value.replace(/\D/g, '');
                      field.onChange(unformattedValue ? Number(unformattedValue) / 100 : 0); 
                    }}
                    error={!!errors.salePrice}
                    helperText={errors.salePrice?.message}
                    // disabled={!canEdit}
                  />
                )}
              />
            </div>

            <div  className={styles.dates}>
              <Controller
                name="startDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    label="Data Início"
                    views={["year", "month"]}
                    openTo="year"
                    value={
                      field.value
                        ? dayjs(field.value).tz("America/Sao_Paulo")
                        : null
                    }
                    onChange={(newValue) => {
                      field.onChange(
                        newValue ? newValue.startOf("day").toISOString() : null
                      );
                      createGrid(editObj);
                    }}
                    slotProps={{
                      textField: {
                        // fullWidth: true,
                        variant: "outlined",
                        InputLabelProps: { shrink: true },
                        error: !!errors.startDate,
                        helperText: errors.startDate?.message,
                      },
                    }}
                    // disabled={!canEdit}
                  />
                )}
              />

              <Controller
                name="endDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    label="Data Fim"
                    views={["year", "month"]}
                    openTo="year"
                    value={
                      field.value
                        ? dayjs(field.value).tz("America/Sao_Paulo")
                        : null
                    }
                    onChange={(newValue) => {
                      field.onChange(
                        newValue ? newValue.startOf("day").toISOString() : null
                      );
                      createGrid(editObj);
                    }}
                    slotProps={{
                      textField: {
                        // fullWidth: true,
                        variant: "outlined",
                        InputLabelProps: { shrink: true },
                        error: !!errors.endDate,
                        helperText: errors.endDate?.message,
                      },
                    }}
                    // disabled={!canEdit}
                  />
                )}
              />
            </div>

            { gridArray && gridArray.length > 0 &&
              <PerfilTimeGrid gridArray={gridArray} setGridArray={setGridArray} />
            }

            <div className={styles.divBtn}>
              <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={!gridArray}
              className={styles.btnSave}
              >
                Salvar
              </Button>
            </div>

          </form>

        </div>
      </div>
    </Modal>
  );
};
